import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { Store, select } from '@ngrx/store';
import { Subject } from 'rxjs';
import { filter, switchMap, take, takeUntil } from 'rxjs/operators';

import { Feature } from 'src/app/features/feature-management/models';
import { FeatureManagementService } from 'src/app/features/feature-management/services';
import { PackagesSelectors } from 'src/app/features/packages/store/selectors';
import { HelpFaqActions } from '../../store/actions';
import { HelpFaqSelectors } from '../../store/selectors';
import { getHasAssociatedUsers } from '../../store/selectors/help-faq.selectors';
import { ChatConversationStore } from './chat-conversation.store';

@Component({
  selector: 'app-chat-widget',
  templateUrl: './chat-widget.component.html',
  changeDetection: ChangeDetectionStrategy.Default,
  providers: [ChatConversationStore],
  standalone: false,
})
export class ChatWidgetComponent implements OnInit, OnDestroy {
  protected readonly store: ChatConversationStore;
  protected isEnabled = false;

  private readonly destroy$ = new Subject<void>();

  constructor(
    store: ChatConversationStore,
    private readonly ngrxStore: Store,
    private readonly featureManagementService: FeatureManagementService,
    @Inject(DOCUMENT) private readonly document: Document
  ) {
    this.store = store;
  }

  ngOnInit(): void {
    // Gate on the same condition as session-handoff-page's hasAssociatedUsers:
    // fires only when a participant has been associated, guaranteeing auth is complete.
    this.ngrxStore.pipe(
      select(getHasAssociatedUsers),
      filter((has) => has === true),
      take(1),
      takeUntil(this.destroy$),
      switchMap(() => this.featureManagementService.getIsFeatureFlagEnabled(Feature.HelpAssistWidget))
    ).subscribe((isEnabled) => {
      this.isEnabled = isEnabled;
      if (isEnabled) {
        this.watchWidgetState();
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.document.body.classList.remove('faq-open');
    this.document.querySelector('[inert]')?.removeAttribute('inert');
  }

  private watchWidgetState(): void {
    // We check widget state on a microtask tick by subscribing to a timer
    // Angular's ChangeDetection will run the getter in the template anyway;
    // for side-effects (body class, inert), we watch via a simple approach.
    let prevState = this.store.widgetState();
    const checkState = () => {
      const cur = this.store.widgetState();
      if (cur !== prevState) {
        prevState = cur;
        if (cur === 'opened') {
          this.document.querySelector('app-root > :not(app-chat-widget)')?.setAttribute('inert', '');
          this.document.body.classList.add('faq-open');
          this.loadFaqsIfNeeded();
        } else {
          this.document.querySelector('[inert]')?.removeAttribute('inert');
          this.document.body.classList.remove('faq-open');
        }
      }
      if (!this.destroy$.closed) {
        requestAnimationFrame(checkState);
      }
    };
    requestAnimationFrame(checkState);
  }

  private loadFaqsIfNeeded(): void {
    this.ngrxStore
      .pipe(select(HelpFaqSelectors.getIsFaqLoaded), take(1))
      .subscribe((isLoaded) => {
        if (!isLoaded) {
          this.ngrxStore.select(PackagesSelectors.getActivePackageGuid).pipe(
            filter((guid) => !!guid),
            take(1)
          ).subscribe(() => {
            this.ngrxStore.dispatch(HelpFaqActions.LoadFaqs());
          });
        }
      });
  }
}
